import React, { Component } from 'react';
import { Card, Container, Row, Col } from 'react-bootstrap';
import {
  listMissions,
  getMission,
  listCells,
  listDependsOn,
  getCell,
  upsertCell,
  removeCell,
  addDependsOnEdge,
  removeDependsOnEdge,
} from '../../domain/model';
import MissionGraph, { edgeRfId, newCellPosition } from './missionGraph';
import CellPanel from './cellPanel';
import MissionPanel from './missionPanel';
import DependencyPanel from './dependencyPanel';
import './cells.css';

/**
 * CellsSurface — the core surface of Mission Control.
 *
 * Layout:
 *   [ Card: MissionGraph (flex 1) ][ Card: detail panel (right dock) ]
 *
 * The detail panel is ALWAYS rendered while a mission is active and shows one of three
 * views depending on the selection: a selected cell → CellPanel, a selected dependency
 * edge → DependencyPanel, nothing selected → MissionPanel (mission summary). Deselecting
 * therefore never hides the panel — it falls back to the Mission view.
 *
 * The "+ Cell" button and add-cell modal have been moved to Shell, which already
 * has access to client, selection.missionId, and store.templates. CellsSurface
 * renders only the graph canvas card and the detail panel card.
 *
 * Selection state flows through the shell's selection/onSelect props so that
 * surface-switching preserves selection. The selection shape is:
 *   { missionId: string, cellId: string|null, edge: object|null }
 * Only one of cellId / edge is ever set.
 *
 * Every write here is INCREMENTAL: it calls the client, then applies a pure
 * store reducer through onApplyLocal — never onReload. The mission graph updates
 * in place (no re-fetch, no re-layout, no loading spinner). The editing handlers
 * are stable instance methods that read the current mission id from props at call
 * time, so the MissionGraph prop chain stays referentially stable across renders.
 *
 * Props:
 *   store        — model store (from MissionsNode state)
 *   client       — SystemOfWorkClient instance
 *   selection    — { missionId?, cellId?, edge? } | null
 *   onSelect     — (item) => void
 *   onApplyLocal — (updater: store => store) => void  — incremental local store update
 */
class CellsSurface extends Component {
  constructor(props) {
    super(props);
    this._mounted = false;
    // Local UI state: the Mission panel's × collapses the whole detail dock for more graph room.
    // Selecting a cell/edge re-opens it; a plain deselect (pane click) does NOT.
    this.state = { panelHidden: false };
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  // ---------------------------------------------------------------------------
  // Mission resolution — prefer the selected mission, fall back to the first one
  // ---------------------------------------------------------------------------

  _missionId() {
    const { store, selection } = this.props;
    const missions = store ? listMissions(store) : [];
    return (selection && selection.missionId) || (missions.length > 0 ? missions[0].id : null);
  }

  // ---------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------

  // Select a cell (clearing any edge selection). A null cellId — e.g. a pane click — fully deselects,
  // which falls back to the Mission panel.
  handleSelectCell = (cellId) => {
    const { onSelect } = this.props;
    // A real selection re-opens a collapsed dock; a pane-click deselect (cellId null) leaves it as-is.
    if (cellId && this.state.panelHidden) this.setState({ panelHidden: false });
    if (onSelect) onSelect({ missionId: this._missionId(), cellId: cellId || null, edge: null });
  };

  // Select a dependency edge (clearing any cell selection).
  handleSelectEdge = (edge) => {
    const { onSelect } = this.props;
    if (edge && this.state.panelHidden) this.setState({ panelHidden: false });
    if (onSelect) onSelect({ missionId: this._missionId(), cellId: null, edge: edge || null });
  };

  // Close the cell / dependency panel — deselect everything, which falls back to the Mission panel.
  handleClosePanel = () => {
    const { onSelect } = this.props;
    if (onSelect) onSelect({ missionId: this._missionId(), cellId: null, edge: null });
  };

  // Switch the active mission (the Mission panel's switcher). Clears any cell/edge selection.
  handleSelectMission = (missionId) => {
    const { onSelect } = this.props;
    if (onSelect) onSelect({ missionId, cellId: null, edge: null });
  };

  // ---------------------------------------------------------------------------
  // Incremental edits — client write, then onApplyLocal(pure reducer)
  // ---------------------------------------------------------------------------

  // Wire a dependency: dependent depends on prerequisite (the arrow is drawn dependent → prerequisite,
  // arrowhead on the prerequisite).
  handleAddDependency = async (dependentId, prerequisiteId) => {
    const { client, onApplyLocal, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    // Never create a DUPLICATE dependency. The local reducer dedups by (from,to), but the server write
    // does not — so re-adding an existing pair would commit a second edge that surfaces on the next
    // reload. Bail out entirely (no server write) when the edge already exists.
    const existing = store ? listDependsOn(store, missionId) : [];
    if (existing.some(e => e.from_id === dependentId && e.to_id === prerequisiteId)) return;
    try {
      const edge = await client.addDependsOn(missionId, dependentId, prerequisiteId);
      if (onApplyLocal) onApplyLocal(s => addDependsOnEdge(s, missionId, edge));
    } catch (_e) {
      // Ignore; the graph reflects server state on the next genuine reload.
    }
  };

  // Add a DEPENDENT cell — a new cell that depends on this one (this cell is the prerequisite) — and
  // wire "new depends on this" in one gesture. Both client calls are awaited first, then the new cell
  // AND its depends_on edge are applied to the store in a SINGLE local update so the node and its edge
  // appear together in one refresh — not as two separate pops. The new cell is placed to the RIGHT of
  // its parent (and that x/y persisted) so it lands beside it rather than at the dagre origin; parentPos
  // is the parent's current rendered position, threaded from CellNode.
  handleAddDependent = async (cellId, parentPos) => {
    const { client, onApplyLocal, onSelect } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    try {
      const pos = newCellPosition(parentPos, 'dependent');
      const dependent = await client.addCell(missionId, {
        title: 'New cell',
        cellWorkflow: null,
        ...(pos || {}),
      });
      // new cell (dependent) depends on cellId (prerequisite): from_id = new, to_id = this.
      const edge = await client.addDependsOn(missionId, dependent.element_id, cellId);
      if (onApplyLocal) {
        onApplyLocal(s => addDependsOnEdge(upsertCell(s, missionId, dependent), missionId, edge));
      }
      if (onSelect) onSelect({ missionId, cellId: dependent.element_id, edge: null });
    } catch (_e) {
      // Ignore; reflected on next reload.
    }
  };

  // Add a PREREQUISITE cell — a new cell that this one depends on (the new cell is the prerequisite) —
  // and wire "this depends on new" in one gesture. Mirrors handleAddDependent but with the dependency
  // pointing the other way and the new cell placed to the LEFT of its parent; the new cell + its edge
  // are applied in a SINGLE local update.
  handleAddPrerequisite = async (cellId, parentPos) => {
    const { client, onApplyLocal, onSelect } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    try {
      const pos = newCellPosition(parentPos, 'prerequisite');
      const prerequisite = await client.addCell(missionId, {
        title: 'New cell',
        cellWorkflow: null,
        ...(pos || {}),
      });
      // cellId (dependent) depends on the new cell (prerequisite): from_id = this, to_id = new.
      const edge = await client.addDependsOn(missionId, cellId, prerequisite.element_id);
      if (onApplyLocal) {
        onApplyLocal(s => addDependsOnEdge(upsertCell(s, missionId, prerequisite), missionId, edge));
      }
      if (onSelect) onSelect({ missionId, cellId: prerequisite.element_id, edge: null });
    } catch (_e) {
      // Ignore; reflected on next reload.
    }
  };

  // Persist a dragged cell's new position (optimistic local upsert + server write) so the graph never
  // re-layouts that cell on later edits.
  handleMoveCell = async (cellId, position) => {
    const { client, onApplyLocal, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId || !position) return;
    const cell = store ? getCell(store, missionId, cellId) : null;
    if (!cell) return;
    const merged = { ...cell, props: { ...(cell.props || {}), x: position.x, y: position.y } };
    if (onApplyLocal) onApplyLocal(s => upsertCell(s, missionId, merged));
    try {
      await client.setCellProps(missionId, cellId, { x: position.x, y: position.y });
    } catch (_e) {
      // Ignore; the optimistic local position stands until the next reload.
    }
  };

  // Remove a cell (and its incident edges) from the mission.
  handleRemoveCell = async (cellId) => {
    const { client, onApplyLocal, onSelect, selection, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    const cell = store ? getCell(store, missionId, cellId) : null;
    try {
      await client.deleteCell(missionId, cellId, cell && cell.rev);
    } catch (_e) {
      // Ignore; reflected on next reload.
    }
    if (onApplyLocal) onApplyLocal(s => removeCell(s, missionId, cellId));
    if (selection && selection.cellId === cellId && onSelect) {
      onSelect({ missionId, cellId: null, edge: null });
    }
  };

  // Remove a dependency edge (identified by element_id; located in the store by from/to). When the
  // removed edge is the selected one, deselect so the panel falls back to the Mission view.
  handleRemoveEdge = async (edge) => {
    const { client, onApplyLocal, onSelect, selection, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId || !edge) return;
    // Delete EVERY server edge for this (from,to) pair — not just the one passed — so any pre-existing
    // duplicates are cleaned up in a single remove and don't resurface on the next reload.
    const all = store
      ? listDependsOn(store, missionId).filter(e => e.from_id === edge.from_id && e.to_id === edge.to_id)
      : [];
    const targets = all.length ? all : [edge];
    for (const e of targets) {
      try {
        if (e.element_id) await client.deleteEdge(missionId, e.element_id, e.rev);
      } catch (_e) {
        // Ignore; reflected on next reload.
      }
    }
    if (onApplyLocal) onApplyLocal(s => removeDependsOnEdge(s, missionId, edge.from_id, edge.to_id));
    const sel = selection && selection.edge;
    if (sel && sel.from_id === edge.from_id && sel.to_id === edge.to_id && onSelect) {
      onSelect({ missionId, cellId: null, edge: null });
    }
  };

  render() {
    const { store, client, selection, onApplyLocal } = this.props;

    // Resolve mission id: prefer the one in selection, fall back to first available
    const missions = store ? listMissions(store) : [];
    const selMissionId = this._missionId();
    const selCellId = (selection && selection.cellId) || null;
    const selEdge = (selection && selection.edge) || null;

    // Cells and dependency edges for the active mission
    const cells = (store && selMissionId) ? listCells(store, selMissionId) : [];
    const dependsOnEdges = (store && selMissionId) ? listDependsOn(store, selMissionId) : [];

    // Selected cell vertex / active mission vertex / selected edge id
    const selectedCell = (store && selMissionId && selCellId)
      ? getCell(store, selMissionId, selCellId)
      : null;
    const mission = (store && selMissionId) ? getMission(store, selMissionId) : null;
    const selectedEdgeId = selEdge ? edgeRfId(selEdge) : null;

    // The detail dock shows unless the user collapsed it (Mission panel ×) AND nothing is selected.
    // A live cell/edge selection always shows its panel.
    const showPanel = !!(selectedCell || selEdge || !this.state.panelHidden);

    if (missions.length === 0) {
      return (
        <div className="mission-control-empty-state">
          <div className="mission-control-surface-title text-muted">Missions</div>
          <span>No missions yet. Use &ldquo;+ New mission&rdquo; above to get started.</span>
        </div>
      );
    }

    return (
      <Container fluid className="mc-cells-container">
        <Row className="mc-grid-row">
          {/* Mission graph — full-bleed canvas in a flat card */}
          <Col className="d-flex flex-column">
            <Card className="mc-graph-card">
              <Card.Body className="mc-card-flush">
                <MissionGraph
                  key={selMissionId}
                  cells={cells}
                  dependsOnEdges={dependsOnEdges}
                  selectedId={selCellId}
                  selectedEdgeId={selectedEdgeId}
                  onSelect={this.handleSelectCell}
                  onSelectEdge={this.handleSelectEdge}
                  onAddDependency={selMissionId ? this.handleAddDependency : undefined}
                  onAddPrerequisite={selMissionId ? this.handleAddPrerequisite : undefined}
                  onAddDependent={selMissionId ? this.handleAddDependent : undefined}
                  onRemoveCell={selMissionId ? this.handleRemoveCell : undefined}
                  onRemoveEdge={selMissionId ? this.handleRemoveEdge : undefined}
                  onMoveCell={selMissionId ? this.handleMoveCell : undefined}
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Detail dock — shown unless collapsed via the Mission panel's ×. One of: the selected
              cell (CellPanel), the selected dependency edge (DependencyPanel), or the mission summary
              (MissionPanel) when nothing is selected. */}
          {showPanel && (
          <Col className="d-flex flex-column detail-panel-col">
            <Card className="mc-panel-card">
              <Card.Body>
                {selectedCell ? (
                  <CellPanel
                    key={selectedCell.element_id}
                    cell={selectedCell}
                    missionId={selMissionId}
                    client={client}
                    onClose={this.handleClosePanel}
                    onApplyLocal={onApplyLocal}
                    templates={store ? store.templates : {}}
                  />
                ) : selEdge ? (
                  <DependencyPanel
                    key={selectedEdgeId}
                    edge={selEdge}
                    cells={cells}
                    onClose={this.handleClosePanel}
                  />
                ) : (
                  <MissionPanel
                    key={selMissionId}
                    mission={mission}
                    missionId={selMissionId}
                    cells={cells}
                    dependsOnEdges={dependsOnEdges}
                    client={client}
                    onApplyLocal={onApplyLocal}
                    onClose={() => this.setState({ panelHidden: true })}
                    missions={missions}
                    selectedMissionId={selMissionId}
                    onSelectMission={this.handleSelectMission}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>
          )}
        </Row>
      </Container>
    );
  }
}

export default CellsSurface;
